import { X, SendHorizontal, Keyboard, AudioLines, Languages } from "lucide-react";
import { Presence } from "@/components/Presence";
import { SourceChips } from "@/components/SourceChips";
import { Console } from "@/components/session/Console";

const Round = ({ icon: Icon, onClick, testId, label }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    aria-label={label}
    className="flex h-9 w-9 items-center justify-center rounded-full border transition-colors active:bg-white/[0.06]"
    style={{ borderColor: "var(--border-linen)", color: "var(--text-dim)" }}
  >
    <Icon size={15} />
  </button>
);

export const SessionMobile = ({ state, labName, accent = "#C87D55", mode, setMode, turns, draft, setDraft, onSend, onMic, end, cycleLang, langLabel, openings, ask, convo, toggleConvo, cancelRecording, elapsed, showTranscript, setSource, bottom, busy, aiConfigured, optIn, read, toggleRead, passages, togglePassages, sourcesAvailable, stopSpeaking }) => {
  const started = turns.length > 0;
  const voice = mode === "voice";

  return (
    <div
      className="grain lamp relative flex flex-col overflow-hidden"
      style={{ height: "100dvh", maxHeight: "100dvh", "--lab-accent": accent, background: "var(--bg-primary)" }}
      data-testid="session-screen"
    >
      <header className="relative z-10 flex shrink-0 items-start justify-between gap-3 px-5 pt-4"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.9rem)" }}
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="h-9 w-[2px] shrink-0 rounded-full" style={{ background: `linear-gradient(180deg, transparent, ${accent}, transparent)` }} />
          <div className="min-w-0">
            <p className="eyebrow truncate" data-testid="session-lab-name">{labName}</p>
            <p className="mt-1 truncate font-mono text-[10px] tracking-wider" style={{ color: "var(--text-faint)" }}>
              anonymous · {langLabel.short.toLowerCase()}{optIn ? " · kept" : " · nothing kept"}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <Round testId="language-toggle-btn" onClick={cycleLang} icon={Languages} label="Change language" />
          <Round testId="end-session-btn" onClick={end} icon={X} label="Leave the conversation" />
        </div>
      </header>
      <span className="sr-only" data-testid="language-toggle-value">{langLabel.label}</span>

      {!aiConfigured && (
        <p data-testid="ai-not-configured-banner" className="relative z-10 mx-5 mt-4 rounded-2xl px-4 py-2.5 text-xs leading-relaxed" style={{ border: "1px solid rgba(212,163,89,0.3)", background: "rgba(212,163,89,0.08)", color: "#D4A359" }}>
          My voice isn't connected yet — a curator still needs to add a key.
        </p>
      )}

      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain" data-testid={voice ? "voice-transcript" : "text-chat"}>
        <div className="shrink-0 pt-1">
          <Presence state={state} accent={accent} height={started && showTranscript ? 150 : 190} />
        </div>

        {!started ? (
          <div className="rise rise-2 px-6 pb-6">
            <h1 className="font-display text-[29px] font-light leading-[1.14] tracking-tight" style={{ color: "var(--text-main)" }}>
              What stayed<br />with you?
            </h1>
            <p className="mt-3 max-w-[20rem] text-[13.5px] leading-[1.75]" style={{ color: "var(--text-dim)" }}>
              Speak freely. I keep what this Lab has said, and I'll hand it back to you — nothing invented, nothing borrowed from elsewhere.
            </p>

            {openings?.length > 0 && (
              <div className="mt-7" data-testid="openings">
                <p className="font-mono text-[9.5px] uppercase tracking-[0.26em]" style={{ color: "var(--text-faint)" }}>ways in</p>
                <ul className="mt-3">
                  {openings.map((o, i) => (
                    <li key={o} className={`rise rise-${(i % 3) + 2}`}>
                      <button
                        data-testid={`opening-${i + 1}`}
                        onClick={() => ask(o)}
                        className="group flex w-full items-start gap-3 border-t py-3.5 text-left"
                        style={{ borderColor: "var(--border-linen)" }}
                      >
                        <span className="mt-[3px] font-mono text-[9.5px]" style={{ color: accent }}>{String(i + 1).padStart(2, "0")}</span>
                        <span className="font-display text-[17px] leading-snug" style={{ color: "var(--text-dim)" }}>{o}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6 px-6 pb-4">
            {(showTranscript ? (voice && !read ? turns.slice(-2) : turns) : []).map((t, i) =>
              t.role === "user" ? (
                <p key={i} className="ember-in ml-auto max-w-[82%] rounded-[20px] rounded-br-sm px-4 py-2.5 text-[13.5px] leading-relaxed" style={{ background: "rgba(244,239,230,0.055)", color: "var(--text-dim)" }}>
                  {t.text}
                </p>
              ) : (
                <div key={i} className="ember-in space-y-3 border-l pl-4" style={{ borderColor: `${accent}55` }}>
                  <p className="font-display text-[19px] font-light leading-[1.5]" data-testid="griot-response" style={{ color: "var(--text-main)" }}>{t.text}</p>
                  {passages && sourcesAvailable && <SourceChips sources={t.sources} onOpen={setSource} />}
                </div>
              )
            )}
            {state === "reflecting" && (
              <p className="font-mono text-[10px] uppercase tracking-[0.26em]" style={{ color: "var(--text-faint)" }}>gathering the thread…</p>
            )}
            <div ref={bottom} />
          </div>
        )}
      </div>

      <div
        className="relative z-10 shrink-0 px-4 pt-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)", background: "linear-gradient(180deg, transparent, rgba(12,12,11,0.85) 40%)" }}
      >
        {voice ? (
          <Console
            compact
            state={state}
            convo={convo}
            toggleConvo={toggleConvo}
            onMic={onMic}
            cancelRecording={cancelRecording}
            elapsed={elapsed}
            busy={busy}
            read={read}
            toggleRead={toggleRead}
            passages={passages}
            togglePassages={togglePassages}
            sourcesAvailable={sourcesAvailable}
            stopSpeaking={stopSpeaking}
            trailing={<Round testId="mode-toggle-btn" onClick={() => setMode("text")} icon={Keyboard} label="Write instead" />}
          />
        ) : (
          <div className="flex items-center gap-2">
            <div className="glass flex flex-1 items-center gap-2 rounded-full p-1.5 pl-4">
              <input
                data-testid="text-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSend()}
                placeholder="Write to Griot…"
                className="min-w-0 flex-1 bg-transparent text-[14px] focus:outline-none"
                style={{ color: "var(--text-main)" }}
              />
              <button data-testid="text-send-btn" onClick={onSend} disabled={busy} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full disabled:opacity-50" style={{ backgroundColor: accent, color: "#0C0C0B" }}>
                <SendHorizontal size={16} />
              </button>
            </div>
            <Round testId="mode-toggle-btn" onClick={() => setMode("voice")} icon={AudioLines} label="Back to speaking" />
          </div>
        )}
      </div>
    </div>
  );
};
